import { useCallback, useSyncExternalStore } from 'react';

function matcher(query: string): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
}

/**
 * Whether a media query matches now, and again whenever it changes. Where there is no matchMedia
 * (a test DOM) nothing matches, which is the narrow layout.
 *
 * The structure of a layout is decided here, in script, rather than only in CSS: CSS can hide an
 * element, but a hidden copy is still a second landmark in the page for anything that reads the DOM,
 * and a test DOM cannot see that it is hidden.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      const list = matcher(query);
      if (list === null) return () => {};
      list.addEventListener('change', notify);
      return () => list.removeEventListener('change', notify);
    },
    [query],
  );
  const read = useCallback(() => matcher(query)?.matches ?? false, [query]);
  return useSyncExternalStore(subscribe, read);
}
